<template>
    <div id="map" style="height: 85vh;">
        <div class="layer-switch">
            <select v-model="currentMapLayer">
                <option value="mapbox">Mapbox</option>
                <option value="google">Google</option>
            </select>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { nextTick } from 'vue';
import { drawPolygon } from "@/utils/drawPolygons";

export default {
    name: "FieldOverviewMap",
    props: {
        field: {
            type: Object,
        },
    },
    data() {
        return {
            map: null,
            polygonLayer: null,
            latitude: "",
            longitude: "",
            zoom: 14,
            currentMapLayer: 'google',
            mapboxLayer: null,
            googleLayer: null,
            mapUrls: {
                google: {
                    url: "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
                    attribution: "Tiles © Google",
                    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
                },
            },
        };
    },
    mounted() {
        this.initializeMap();
    },
    methods: {
        initializeMap() {
            if (this.field && this.field.polygons && this.field.polygons.length > 0) {
                this.latitude = this.field.polygons[0].latitude;
                this.longitude = this.field.polygons[0].longitude;
            } else {
                this.latitude = 0;
                this.longitude = 0;
            }

            this.map = L.map('map').setView([this.latitude, this.longitude], this.zoom);

            const mapboxAccessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
            this.mapboxLayer = L.tileLayer(
                `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${mapboxAccessToken}`,
                {
                    attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    tileSize: 512,
                    zoomOffset: -1,
                    maxZoom: 19,
                }
            );

            this.googleLayer = L.tileLayer(this.mapUrls.google.url, {
                attribution: this.mapUrls.google.attribution,
                subdomains: this.mapUrls.google.subdomains,
                maxZoom: 19
            });

            this.updateMapLayer(this.currentMapLayer);

            if (this.field && this.field.polygons && this.field.polygons.length > 0) {
                const polygonData = drawPolygon(this.field);
                this.polygonLayer = L.polygon(polygonData.latlngs, {
                    color: polygonData.color,
                    fillColor: polygonData.fillColor,
                }).addTo(this.map);
                this.polygonLayer.bindPopup(polygonData.text);
            }
        },
        updateMapLayer(newLayer) {
            if (this.map) {
                if (this.map.hasLayer(this.mapboxLayer)) {
                    this.map.removeLayer(this.mapboxLayer);
                }
                if (this.map.hasLayer(this.googleLayer)) {
                    this.map.removeLayer(this.googleLayer);
                }

                if (newLayer === "mapbox") {
                    this.map.addLayer(this.mapboxLayer);
                } else if (newLayer === "google") {
                    this.map.addLayer(this.googleLayer);
                }
            }
            else {
                this.initializeMap();
            }
        },
        refreshMap() {
            this.$nextTick(() => {
                if (this.map) {
                    this.map.invalidateSize();
                }
            });
        },
    },
    watch: {
        field() {
            if (this.field && this.field.polygons && this.field.polygons.length > 0) {
                this.latitude = this.field.polygons[0].latitude;
                this.longitude = this.field.polygons[0].longitude;
                this.map.setView([this.latitude, this.longitude], this.zoom);

                if (this.polygonLayer) {
                    this.polygonLayer.remove();
                }

                const polygonData = drawPolygon(this.field);
                this.polygonLayer = L.polygon(polygonData.latlngs, {
                    color: polygonData.color,
                    fillColor: polygonData.fillColor,
                }).addTo(this.map);
                this.polygonLayer.bindPopup(polygonData.text);
            }
        },
        currentMapLayer(newVal) {
            if (this.map) {
                this.updateMapLayer(newVal);
            }
        },
    }
};
</script>

<style>
.layer-switch {
  position: absolute;
  bottom: 25px;
  left: 50px;
  z-index: 1000;
  background-color: white;
  padding: 5px;
  border-radius: 5px;
}
.map-container {
    position: relative;
}
</style>